import React, { CSSProperties, ReactNode } from 'react';
import classNames from 'classnames';
import { Icon, IconName } from './Icon';

interface FormProps extends React.FormHTMLAttributes<HTMLFormElement> {
  testId?: string;
  // Bronson's floating-label layout. Accepted and ignored: labels here always
  // sit above their field.
  floatingLabel?: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- `floatingLabel` is taken out so it never reaches the DOM
export function Form({ testId, floatingLabel, className, ...otherProps }: FormProps) {
  return <form {...otherProps} data-testid={testId} className={classNames('ec-form', className)} />;
}

interface FormFieldProps {
  labelText?: ReactNode;
  labelForId?: string;
  testId?: string;
  // Bronson's field type. Accepted and ignored.
  type?: string;
  style?: CSSProperties;
  className?: string;
  children?: ReactNode;
}

export function FormField({ labelText, labelForId, testId, style, className, children }: FormFieldProps) {
  return (
    <div data-testid={testId} style={style} className={classNames('ec-form-field', className)}>
      {labelText ? <label className="ec-form-field__label" htmlFor={labelForId}>{labelText}</label> : null}
      <div className="ec-form-field__control">{children}</div>
    </div>
  );
}

interface FormFieldLabelProps extends React.LabelHTMLAttributes<HTMLLabelElement> {
  testId?: string;
}

// Used throughout the app to display values, not only as field labels. The
// children are rendered directly, with no wrapper, because a test relies on
// the first child element being the copy icon.
export function FormFieldLabel({ testId, className, ...otherProps }: FormFieldLabelProps) {
  return <label {...otherProps} data-testid={testId} className={classNames('ec-text', className)} />;
}

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  testId?: string;
  addonIcon?: IconName;
  // Bronson styling flag. Accepted and ignored.
  noBackground?: boolean;
}

// className goes on the wrapper, as it did with Bronson's input.
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- `noBackground` is taken out so it never reaches the DOM
export function Input({ testId, addonIcon, noBackground, className, type = 'text', ...otherProps }: InputProps) {
  return (
    <div className={classNames('ec-input', addonIcon && 'ec-input--with-addon', className)}>
      <input {...otherProps} type={type} data-testid={testId} className="ec-input__field" />
      {addonIcon ? <Icon name={addonIcon} className="ec-input__addon" /> : null}
    </div>
  );
}

interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  testId?: string;
}

export function Textarea({ testId, className, ...otherProps }: TextareaProps) {
  return <textarea {...otherProps} data-testid={testId} className={classNames('ec-textarea', className)} />;
}

interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  testId?: string;
}

export function Select({ testId, className, children, ...otherProps }: SelectProps) {
  return (
    <div className={classNames('ec-select', className)}>
      <select {...otherProps} data-testid={testId} className="ec-select__field">
        {children}
      </select>
      <Icon name="arrow-down" className="ec-select__chevron" />
    </div>
  );
}

interface SelectItemProps extends React.OptionHTMLAttributes<HTMLOptionElement> {
  testId?: string;
}

Select.Item = function SelectItem({ testId, ...otherProps }: SelectItemProps) {
  return <option {...otherProps} data-testid={testId} />;
};

interface ChoiceProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  testId?: string;
  children?: ReactNode;
}

export function Checkbox({ testId, className, children, ...otherProps }: ChoiceProps) {
  return (
    <label className={classNames('ec-choice', className)}>
      <input {...otherProps} type="checkbox" data-testid={testId} className="ec-choice__input" />
      {children ? <span className="ec-choice__label">{children}</span> : null}
    </label>
  );
}

export function CheckboxGroup({ children }: { children?: ReactNode }) {
  return <div className="ec-choice-group">{children}</div>;
}

export function RadioButton({ testId, className, children, ...otherProps }: ChoiceProps) {
  return (
    <label className={classNames('ec-choice ec-choice--radio', className)}>
      <input {...otherProps} type="radio" data-testid={testId} className="ec-choice__input" />
      {children ? <span className="ec-choice__label">{children}</span> : null}
    </label>
  );
}
